import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, Clock, AlertCircle, ExternalLink, Search, RotateCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useRoles } from '@/hooks/useRoles';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface JobBoard {
  id: string;
  name: string;
  url: string;
  category: 'design_niche' | 'general';
  description?: string;
  logo_url?: string;
  country_scope?: string;
  remote_focus: boolean;
  has_rss: boolean;
  status: 'active' | 'dead_link' | 'unknown';
  source: 'notion' | 'scraper';
  last_checked_at?: string;
  created_at: string;
}

export default function AdminJobBoards() {
  const { toast } = useToast();
  const { isAdmin, loading: rolesLoading } = useRoles();
  const [jobBoards, setJobBoards] = useState<JobBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [scrapeProgress, setScrapeProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Check if user is admin
  if (rolesLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access denied. This page is restricted to administrators only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  useEffect(() => {
    loadJobBoards();
  }, [page, categoryFilter, statusFilter, searchTerm]);

  const loadJobBoards = async () => {
    try {
      let query = supabase
        .from('job_boards')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((page - 1) * limit, page * limit - 1);

      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (searchTerm) {
        query = query.ilike('name', `%${searchTerm}%`);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error loading job boards:', error);
        toast({
          title: "Error",
          description: "Failed to load job boards",
          variant: "destructive",
        });
      } else {
        setJobBoards((data || []) as JobBoard[]);
        setTotal(count || 0);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromNotion = async () => {
    setImportLoading(true);
    setImportProgress(0);

    try {
      setImportProgress(20);

      const { data, error } = await supabase.functions.invoke('import-job-boards', {
        body: {}
      });

      setImportProgress(80);

      if (error) throw error;

      setImportProgress(100);
      
      toast({
        title: "Import Complete",
        description: `Imported ${data.imported} job boards from Notion. ${data.skipped} skipped, ${data.errors} errors.`,
        duration: 5000,
      });

      loadJobBoards(); // Refresh the list

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import Failed",
        description: "Failed to import job boards from Notion",
        variant: "destructive",
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleScrapeJobBoards = async () => {
    setScrapeLoading(true);
    setScrapeProgress(0);

    try {
      setScrapeProgress(20);

      const { data, error } = await supabase.functions.invoke('scrape-job-boards', {
        body: {}
      });

      setScrapeProgress(80);

      if (error) throw error;

      setScrapeProgress(100);
      
      toast({
        title: "Scraping Complete",
        description: `Scraped ${data.scraped} new job boards. ${data.errors} errors.`,
        duration: 5000,
      });

      loadJobBoards(); // Refresh the list

    } catch (error) {
      console.error('Scraping error:', error);
      toast({
        title: "Scraping Failed",
        description: "Failed to scrape job boards",
        variant: "destructive",
      });
    } finally {
      setScrapeLoading(false);
    }
  };

  const handleRecheck = async (boardId: string, url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const status = response.ok ? 'active' : 'dead_link';

      await supabase
        .from('job_boards')
        .update({ 
          status, 
          last_checked_at: new Date().toISOString() 
        })
        .eq('id', boardId);

      toast({
        title: "Recheck Complete",
        description: `Status updated to ${status}`,
      });

      loadJobBoards();
    } catch (error) {
      console.error('Recheck error:', error);
      toast({
        title: "Recheck Failed",
        description: "Failed to recheck job board",
        variant: "destructive",
      });
    }
  };

  const handleDisable = async (boardId: string) => {
    try {
      await supabase
        .from('job_boards')
        .update({ status: 'dead_link' })
        .eq('id', boardId);

      toast({
        title: "Job Board Disabled",
        description: "Job board marked as dead link",
      });

      loadJobBoards();
    } catch (error) {
      console.error('Disable error:', error);
      toast({
        title: "Disable Failed",
        description: "Failed to disable job board",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'dead_link':
        return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />Dead Link</Badge>;
      case 'unknown':
        return <Badge variant="secondary"><AlertCircle className="h-3 w-3 mr-1" />Unknown</Badge>;
      default:
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Job Boards Management</h1>
        <p className="text-muted-foreground">
          Import from Notion, scrape from web, and manage job board directory.
        </p>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Import from Notion</CardTitle>
            <CardDescription>
              Import job boards from the canonical Notion database
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {importLoading && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Importing from Notion...</p>
                <Progress value={importProgress} className="w-full" />
              </div>
            )}
            <Button
              onClick={handleImportFromNotion}
              disabled={importLoading}
              className="w-full"
            >
              {importLoading ? "Importing..." : "Import from Notion"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Scrape Job Boards</CardTitle>
            <CardDescription>
              Discover and scrape new job boards from the web
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {scrapeLoading && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Scraping job boards...</p>
                <Progress value={scrapeProgress} className="w-full" />
              </div>
            )}
            <Button
              onClick={handleScrapeJobBoards}
              disabled={scrapeLoading}
              className="w-full"
            >
              {scrapeLoading ? "Scraping..." : "Scrape Job Boards"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search job boards..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="design_niche">Design Niche</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="dead_link">Dead Link</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Job Boards List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Job Boards
            <Badge variant="outline">{total} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Progress value={50} className="w-32" />
            </div>
          ) : (
            <div className="space-y-4">
              {jobBoards.map((board) => (
                <div key={board.id} className="flex items-center gap-4 p-4 border rounded-lg">
                  {board.logo_url && (
                    <img 
                      src={board.logo_url} 
                      alt={board.name}
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-sm truncate">{board.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {board.category}
                      </Badge>
                      {board.remote_focus && (
                        <Badge variant="secondary" className="text-xs">Remote</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {board.description || board.url}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusBadge(board.status)}
                      <span className="text-xs text-muted-foreground">
                        {board.country_scope && `${board.country_scope} • `}
                        {board.source}
                        {board.last_checked_at && ` • Checked ${new Date(board.last_checked_at).toLocaleDateString()}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRecheck(board.id, board.url)}
                    >
                      <RotateCw className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(board.url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDisable(board.id)}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {jobBoards.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No job boards found. Try importing from Notion or scraping from the web.
                </div>
              )}

              {/* Pagination */}
              {total > limit && (
                <div className="flex justify-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm">
                    Page {page} of {Math.ceil(total / limit)}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= Math.ceil(total / limit)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}